/**
 * SkillDelivery Platform
 *
 * Copyright Â© 2025-2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * Community Page - Unified professional networking hub
 * Conference Sprint S1a: Renamed from x-Connect to Community
 * Conference Sprint S1c: Entity-aware headers and descriptions
 * Conference Sprint S1d: Fix duplicate suggestions bug (frontend deduplication)
 *
 * Tabs (Story Flow - Conference Sprint S1b):
 * - My Network: What you have (Network Intelligence stats, mentorship, opportunities)
 * - Discover: What's possible (AI-powered connection suggestions)
 * - Connect: Who's reaching out (Requests + Connections + Notifications merged)
 * - Activity: What's happening (Activity feed placeholder)
 * - Goals: What drives matching (Professional goals)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bell, UserPlus, Check, X, ChevronRight,
  AlertCircle, RefreshCw, Sparkles, Target, Network,
  Users,
  MessageCircle, ExternalLink, Upload, Link2,
  Archive, Building2, Briefcase
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import XConnectGoals from '../components/workspace/xconnect/XConnectGoals';
import LoadingSpinner from '../components/common/LoadingSpinner';
import MatchExplanation from '../components/community/MatchExplanation';
import DirectChatModal from '../components/community/DirectChatModal';
import ProfileShareModal from '../components/community/ProfileShareModal';
import ConsentModal from '../components/network/ConsentModal';
import LinkedInConverter from '../components/network/LinkedInConverter';
import NetworkOverview from '../components/network/NetworkOverview';
import NetworkMentorship from '../components/network/NetworkMentorship';
import CompanyReach from '../components/network/CompanyReach';
import ImportStatusBanner from '../components/network/ImportStatusBanner';
import { getTabsForEntity } from '../config/communityTabs';
import '../components/workspace/xconnect/XConnect.css';

/**
 * Entity-specific content helper - Conference Sprint S1c
 * Returns page content customized for each entity type
 */
const getEntityContent = (entityType, vertical, entityName) => {
  const content = {
    individual: {
      title: 'Community',
      subtitle: `Your professional network in the ${vertical} ecosystem`,
      networkLabel: 'My Network',
      discoverLabel: 'Discover',
      connectLabel: 'Connect',
      activityLabel: 'Activity',
      goalsLabel: 'Goals',
      networkDescription: 'Your professional connections and career intelligence',
      discoverDescription: 'AI-powered career opportunities and suggestions',
      connectDescription: 'Professional connections and requests',
    },
    university: {
      title: 'Community',
      subtitle: `${entityName}'s institutional network in ${vertical} education`,
      networkLabel: 'Institutional Network',
      discoverLabel: 'Opportunities',
      connectLabel: 'Partners',
      activityLabel: 'Activity',
      goalsLabel: 'Goals',
      networkDescription: 'Your institutional connections and placement intelligence',
      discoverDescription: 'Placement & collaboration opportunities',
      connectDescription: 'Industry partners and recruiters',
    },
    organization: {
      title: 'Community',
      subtitle: `${entityName}'s talent network in the ${vertical} industry`,
      networkLabel: 'Talent Network',
      discoverLabel: 'Talent Pool',
      connectLabel: 'Candidates',
      activityLabel: 'Activity',
      goalsLabel: 'Goals',
      networkDescription: 'Your talent pipeline and industry connections',
      discoverDescription: 'Talent & training opportunities',
      connectDescription: 'Candidates and training partners',
    },
    institute: {
      title: 'Community',
      subtitle: `${entityName}'s training network in ${vertical} development`,
      networkLabel: 'Training Ecosystem',
      discoverLabel: 'Opportunities',
      connectLabel: 'Clients',
      activityLabel: 'Activity',
      goalsLabel: 'Goals',
      networkDescription: 'Your training ecosystem and learner network',
      discoverDescription: 'Learners & corporate training opportunities',
      connectDescription: 'Clients and corporate partners',
    },
  };

  return content[entityType] || content.individual;
};

const CommunityPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Entity context for personalized content - Conference Sprint S1c
  const entityType = user?.entityType || user?.entity_type || 'individual';
  const vertical = user?.vertical || 'Platform';
  const entityName = user?.organizationName || user?.organization_name || user?.name || 'Your';
  const pageContent = getEntityContent(entityType, vertical, entityName);

  // Map old tab names to new tab names for backward compatibility
  const tabMapping = {
    'suggestions': 'discover',
    'requests': 'connect',
    'connections': 'connect'
  };

  // Get initial tab from URL or default to 'network' (story flow starts with "what you have")
  const rawTab = searchParams.get('tab') || 'network';
  const initialTab = tabMapping[rawTab] || rawTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  // Community state
  const [suggestions, setSuggestions] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [msgUnreadCounts, setMsgUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Network Intelligence state
  const [networkLoading, setNetworkLoading] = useState(false);
  const [networkImporting, setNetworkImporting] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [networkError, setNetworkError] = useState(null);
  const [networkSubTab, setNetworkSubTab] = useState('overview');
  const [hasNetworkProfile, setHasNetworkProfile] = useState(false);
  const [wikiActivity, setWikiActivity] = useState([]);

  // S7 consent + S5b converter + partial-import banner state
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importStatus, setImportStatus] = useState(null);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Fetch community data
  const fetchCommunityData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use allSettled so one failing endpoint doesn't break the whole page
      const results = await Promise.allSettled([
        api.get('/xconnect/suggestions'),
        api.get('/xconnect/requests/incoming'),
        api.get('/xconnect/requests/outgoing'),
        api.get('/xconnect/connections'),
        api.get('/xconnect/notifications?limit=10'),
        api.get('/xconnect/notifications/unread-count'),
        api.get('/messages/unread/counts')
      ]);

      const [
        suggestionsRes,
        incomingRes,
        outgoingRes,
        connectionsRes,
        notificationsRes,
        unreadRes,
        msgUnreadRes
      ] = results.map(r => r.status === 'fulfilled' ? r.value : null);

      // Handle both response.data?.success and response?.success patterns
      // Null-safe: if a request failed in allSettled, res will be null
      const getSafe = (res, field) => res?.data?.[field] ?? res?.[field] ?? [];
      const getSuccess = (res) => res && (res?.data?.success ?? res?.success);

      if (getSuccess(suggestionsRes)) setSuggestions(getSafe(suggestionsRes, 'suggestions'));
      if (getSuccess(incomingRes)) setIncomingRequests(getSafe(incomingRes, 'requests'));
      if (getSuccess(outgoingRes)) setOutgoingRequests(getSafe(outgoingRes, 'requests'));
      if (getSuccess(connectionsRes)) setConnections(getSafe(connectionsRes, 'connections'));
      if (getSuccess(notificationsRes)) setNotifications(getSafe(notificationsRes, 'notifications'));
      if (getSuccess(unreadRes)) setUnreadCount(unreadRes?.data?.count ?? unreadRes?.count ?? 0);
      if (getSuccess(msgUnreadRes)) setMsgUnreadCounts(msgUnreadRes?.data?.counts ?? msgUnreadRes?.counts ?? {});

      // Log any failed requests for debugging
      const failedCount = results.filter(r => r.status === 'rejected').length;
      if (failedCount > 0) {
        console.warn(`[CommunityPage] ${failedCount} API request(s) failed, page loaded with partial data`);
      }

    } catch (err) {
      console.error('[CommunityPage] Error fetching community data:', err);
      setError('Failed to load community data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWikiActivity = useCallback(async () => {
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';
      const res = await api.get(`/wiki/global?domain=${domain}&limit=5`);
      const entries = res.data?.entries || res.entries || [];
      setWikiActivity(entries.map(e => ({
        id: e.id,
        type: 'wiki_update',
        title: e.source_type === 'scout_agent'
          ? `🔍 Scout found: ${e.topic}`
          : e.source_type === 'seal_auto'
          ? `🤖 SEAL generated: ${e.topic}`
          : `📋 New wiki entry: ${e.topic}`,
        time: e.created_at,
        domain: e.domain
      })));
    } catch (e) {
      setWikiActivity([]);
    }
  }, [user?.vertical]);

  // Fetch Network Intelligence data
  const loadNetworkDashboard = useCallback(async () => {
    if (!user?.id) return;

    try {
      setNetworkLoading(true);
      setNetworkError(null);
      // userId is derived server-side from the auth token — do not send it.
      const response = await api.get('/network/dashboard');

      if (response.success) {
        setDashboard(response.dashboard);
        setHasNetworkProfile(response.hasProfile !== false);
      }
    } catch (error) {
      console.error('[CommunityPage] Error loading network dashboard:', error);
      setNetworkError('Failed to load network data. Have you imported your LinkedIn data?');
    } finally {
      setNetworkLoading(false);
    }
  }, [user?.id]);

  // Upload a converted network CSV. The consent modal + LinkedInConverter
  // component produce the platform-format file; api.uploadNetworkCsv
  // wraps the multipart POST so the URL + token attachment come from a
  // single source of truth (api.js). Do NOT hand-roll fetch here — the
  // prior shape double-prefixed /api and produced 404s in production.
  const uploadNetworkCsv = async (file) => {
    setNetworkImporting(true);
    setNetworkError(null);
    setImportResult(null);
    try {
      const data = await api.uploadNetworkCsv(file);
      if (data.success) {
        setImportResult(data);
        // Refresh dashboard + status banner after successful import.
        await Promise.all([loadNetworkDashboard(), loadImportStatus()]);
      } else {
        setNetworkError(data.error || 'Import failed. Try again or check the file format.');
        await loadImportStatus();
      }
    } catch (error) {
      console.error('[CommunityPage] Error uploading network CSV:', error);
      setNetworkError('Upload failed. Check your connection and try again.');
    } finally {
      setNetworkImporting(false);
    }
  };

  // Read the persistent import-status marker so the UI can show a
  // "your last import did not finish" banner if the user closed the
  // tab mid-upload.
  const loadImportStatus = useCallback(async () => {
    if (!user?.id) return;
    try {
      const resp = await api.get('/network/import-status');
      if (resp.success) setImportStatus(resp.status);
    } catch (error) {
      // Non-fatal — banner just stays hidden.
      console.warn('[CommunityPage] import-status read failed:', error.message);
    }
  }, [user?.id]);

  // Check consent state on Community-tab mount so the modal fires
  // BEFORE the file picker is offered.
  const loadConsentState = useCallback(async () => {
    try {
      const resp = await api.get('/network/consent');
      if (resp.success) setConsentAccepted(!!resp.accepted);
    } catch (error) {
      // If we can't reach the endpoint, err on the side of the modal.
      setConsentAccepted(false);
    }
  }, []);

  const handleImportButtonClick = () => {
    if (consentAccepted) {
      setShowUploadPanel(true);
    } else {
      setShowConsentModal(true);
    }
  };

  const handleConsentAccept = () => {
    setConsentAccepted(true);
    setShowConsentModal(false);
    setShowUploadPanel(true);
  };

  useEffect(() => {
    fetchCommunityData();
    fetchWikiActivity();
  }, [fetchCommunityData, fetchWikiActivity]);

  // Load consent state + persistent import-status banner on mount so
  // the UI can show "your last import did not finish" even if the
  // user closed the tab mid-upload.
  useEffect(() => {
    if (!user?.id) return;
    loadConsentState();
    loadImportStatus();
  }, [user?.id, loadConsentState, loadImportStatus]);

  // Auto-match: check for new matches on page load, then refresh suggestions
  useEffect(() => {
    let cancelled = false;
    const checkForNewMatches = async () => {
      try {
        const response = await api.get('/xconnect/check-matches');
        const data = response?.data || response;
        if (!cancelled && data?.newMatches > 0) {
          console.log(`[Community] Found ${data.newMatches} new matches, refreshing...`);
          fetchCommunityData();
        }
      } catch (err) {
        // Non-fatal - page still works with existing suggestions
      }
    };
    // Small delay so initial data loads first
    const timer = setTimeout(checkForNewMatches, 2000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'network' && !dashboard && !networkLoading) {
      loadNetworkDashboard();
    }
  }, [activeTab, dashboard, networkLoading, loadNetworkDashboard]);

  // Deduplication now handled in backend getSuggestions() with DISTINCT ON

  // Community handlers
  const handleDismissSuggestion = async (suggestionId) => {
    try {
      await api.delete(`/xconnect/suggestions/${suggestionId}`);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    } catch (err) {
      console.error('[CommunityPage] Error dismissing suggestion:', err);
    }
  };

  const handleSendRequest = async (suggestion) => {
    try {
      const response = await api.post('/xconnect/requests', {
        toUserId: suggestion.suggested_user_id,
        connectionType: suggestion.suggestion_type,
        suggestionId: suggestion.id,
        entityType: user?.entityType || user?.entity_type,
        vertical: user?.vertical
      });

      if (response?.data?.success || response?.success) {
        setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
        const newRequest = response?.data?.request || response?.request;
        if (newRequest) {
          setOutgoingRequests(prev => [newRequest, ...prev]);
        }
      }
    } catch (err) {
      console.error('[CommunityPage] Error sending request:', err);
    }
  };

  const handleRespondToRequest = async (requestId, action) => {
    try {
      const response = await api.put(`/xconnect/requests/${requestId}`, { action });

      if (response?.data?.success || response?.success) {
        setIncomingRequests(prev => prev.filter(r => r.id !== requestId));
        const newConnection = response?.data?.connection || response?.connection;
        if (action === 'accepted' && newConnection) {
          setConnections(prev => [newConnection, ...prev]);
        }
      }
    } catch (err) {
      console.error('[CommunityPage] Error responding to request:', err);
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await api.patch(`/xconnect/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[CommunityPage] Error marking notification read:', err);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Dynamic entity-aware tabs from config
  const iconMap = { Network, Sparkles, UserPlus, Bell, Target, Building2, Briefcase };
  const tabConfig = getTabsForEntity(entityType);

  // Compute counts for filtered sub-tabs
  const talentSuggestions = suggestions.filter(s => s.suggested_entity_type?.toLowerCase() === 'individual');
  const partnerSuggestions = suggestions.filter(s => ['institute', 'university'].includes(s.suggested_entity_type?.toLowerCase()));
  const clientSuggestions = suggestions.filter(s => s.suggested_entity_type?.toLowerCase() === 'organization');
  const countMap = {
    suggestions: suggestions.length,
    talentSuggestions: talentSuggestions.length,
    partnerSuggestions: partnerSuggestions.length,
    clientSuggestions: clientSuggestions.length,
    connectCount: incomingRequests.length + connections.length,
    unread: unreadCount
  };

  const tabs = tabConfig.tabs.map(t => ({
    id: t.id,
    label: t.label,
    icon: iconMap[t.icon] || Network,
    count: t.countKey ? (countMap[t.countKey] || 0) : null,
    filterEntities: t.filterEntities || null,
    description: t.description || null
  }));

  // Network Intelligence data. `dashboard.opportunities` (from
  // network.js's WORKED_AT-derived list) is intentionally NOT
  // destructured — CompanyReach reads `overview.topCompanies`
  // instead, per the S5/S6 rewrite. See commit (b) rationale.
  const overview = dashboard?.overview || {};
  const mentorship = dashboard?.mentorship || {};

  if (loading) {
    return <LoadingSpinner message="Loading Community..." />;
  }

  return (
    <div className="community-page-container">
      {/* S7 consent modal — shown when user clicks Import and hasn't
          accepted the current consent version. Blocks the file picker
          from rendering until consent is recorded. */}
      {showConsentModal && (
        <ConsentModal
          onAccept={handleConsentAccept}
          onCancel={() => setShowConsentModal(false)}
        />
      )}

      {/* Page Header - Entity-aware (Conference Sprint S1c) */}
      <div className="community-page-header">
        <div className="header-content">
          <div className="header-title-row">
            <Users size={32} className="header-icon" />
            <div>
              <h1>{pageContent.title}</h1>
              <p>{pageContent.subtitle}</p>
            </div>
          </div>
          <button
            className="refresh-btn-large"
            onClick={() => {
              fetchCommunityData();
              if (activeTab === 'network') loadNetworkDashboard();
            }}
            title="Refresh all data"
          >
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="community-page-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="community-page-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`page-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <tab.icon size={18} />
            <span>{tab.label}</span>
            {tab.count > 0 && <span className="tab-badge">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="community-page-content">
        {/* Discover Tab (renamed from Suggestions) - "What's possible" */}
        {activeTab === 'discover' && (
          <div className="tab-panel discover-panel">
            {suggestions.length === 0 ? (
              <div className="empty-state-large">
                <Sparkles size={48} className="empty-icon" />
                <h3>No suggestions yet</h3>
                <p>Create goals in the Goals tab to receive AI-powered connection suggestions based on your professional objectives.</p>
                <button className="cta-btn" onClick={() => handleTabChange('goals')}>
                  <Target size={18} />
                  Create Goals
                </button>
              </div>
            ) : (
              <div className="suggestions-grid">
                {suggestions.map(suggestion => (
                  <SuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onConnect={() => handleSendRequest(suggestion)}
                    onDismiss={() => handleDismissSuggestion(suggestion.id)}
                    getInitials={getInitials}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Entity-filtered suggestion tabs (Talent, Partners, Clients) */}
        {['talent', 'partners', 'clients'].includes(activeTab) && (() => {
          const currentTab = tabs.find(t => t.id === activeTab);
          const filtered = currentTab?.filterEntities
            ? suggestions.filter(s => currentTab.filterEntities.includes(s.suggested_entity_type?.toLowerCase()))
            : suggestions;
          return (
            <div className="tab-panel discover-panel">
              {currentTab?.description && (
                <p className="tab-description" style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>{currentTab.description}</p>
              )}
              {filtered.length === 0 ? (
                <div className="empty-state-large">
                  <Sparkles size={48} className="empty-icon" />
                  <h3>No {currentTab?.label?.toLowerCase()} suggestions yet</h3>
                  <p>Create goals in the Goals tab to receive AI-powered connection suggestions.</p>
                  <button className="cta-btn" onClick={() => handleTabChange('goals')}>
                    <Target size={18} />
                    Create Goals
                  </button>
                </div>
              ) : (
                <div className="suggestions-grid">
                  {filtered.map(suggestion => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onConnect={() => handleSendRequest(suggestion)}
                      onDismiss={() => handleDismissSuggestion(suggestion.id)}
                      getInitials={getInitials}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Connect Tab (merged Requests + Connections) - "Who's reaching out" */}
        {activeTab === 'connect' && (
          <div className="tab-panel connect-panel">
            {/* Incoming Requests - actionable items first */}
            {incomingRequests.length > 0 && (
              <div className="connect-section">
                <h3 className="section-title">
                  <UserPlus size={18} />
                  Incoming Requests ({incomingRequests.length})
                </h3>
                <div className="requests-grid">
                  {incomingRequests.map(request => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      type="incoming"
                      onAccept={() => handleRespondToRequest(request.id, 'accepted')}
                      onReject={() => handleRespondToRequest(request.id, 'rejected')}
                      getInitials={getInitials}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Active Connections */}
            {connections.length > 0 && (
              <div className="connect-section">
                <h3 className="section-title">
                  <Link2 size={18} />
                  Your Connections ({connections.length})
                </h3>
                <div className="connections-grid">
                  {connections.map(connection => (
                    <ConnectionCard
                      key={connection.id}
                      connection={connection}
                      currentUserId={user?.id || user?._id}
                      getInitials={getInitials}
                      unreadMsgCount={msgUnreadCounts[connection.id] || 0}
                      onChatClosed={() => {
                        setMsgUnreadCounts(prev => ({ ...prev, [connection.id]: 0 }));
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Outgoing Requests */}
            {outgoingRequests.length > 0 && (
              <div className="connect-section">
                <h3 className="section-title">
                  <ChevronRight size={18} />
                  Sent Requests ({outgoingRequests.length})
                </h3>
                <div className="requests-grid">
                  {outgoingRequests.map(request => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      type="outgoing"
                      getInitials={getInitials}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {incomingRequests.length === 0 && connections.length === 0 && outgoingRequests.length === 0 && (
              <div className="empty-state-large">
                <UserPlus size={48} className="empty-icon" />
                <h3>No connections yet</h3>
                <p>Your connections, pending requests, and sent invitations will appear here.</p>
                <button className="cta-btn" onClick={() => handleTabChange('discover')}>
                  <Sparkles size={18} />
                  Discover People
                </button>
              </div>
            )}
          </div>
        )}

        {/* Activity Tab - "What's happening" */}
        {activeTab === 'activity' && (
          <div className="tab-panel activity-panel">
            {notifications.length === 0 && wikiActivity.length === 0 ? (
              <div className="empty-state-large">
                <Bell size={48} className="empty-icon" />
                <h3>No activity yet</h3>
                <p>Your community activity and notifications will appear here.</p>
              </div>
            ) : (
              <div className="activity-content">
                {/* Wiki Activity Section */}
                {wikiActivity.length > 0 && (
                  <div className="activity-section" style={{ marginBottom: '20px' }}>
                    <h3 className="section-title">
                      <Archive size={18} />
                      Domain Intelligence
                    </h3>
                    <div className="activity-list">
                      {wikiActivity.map(item => (
                        <div key={item.id}
                          className="activity-card"
                          style={{ borderLeft: '3px solid #7c3aed', paddingLeft: '12px' }}>
                          <div className="activity-icon" style={{ background: '#f3f0ff' }}>
                            <Archive size={18} style={{ color: '#7c3aed' }} />
                          </div>
                          <div className="activity-info">
                            <span className="activity-title">{item.title}</span>
                            <span className="activity-time">
                              {new Date(item.time).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="activity-section">
                  <h3 className="section-title">
                    <Bell size={18} />
                    Recent Activity
                  </h3>
                  <div className="activity-list">
                    {notifications.map(notification => (
                      <ActivityCard
                        key={notification.id}
                        notification={notification}
                        onMarkRead={() => handleMarkNotificationRead(notification.id)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Network Tab (Network Intelligence) - "What you have" */}
        {activeTab === 'network' && (
          <div className="tab-panel network-panel">
            {networkLoading ? (
              <LoadingSpinner message="Loading network intelligence..." centered={false} />
            ) : networkError && !dashboard ? (
              <div className="network-setup">
                <Network size={48} className="empty-icon" />
                <h3>Set Up Network Intelligence</h3>
                <p>{networkError}</p>
                <div className="setup-instructions">
                  <h4>Get started:</h4>
                  <ol>
                    <li>Read the consent notice — you're responsible for the data you upload about others.</li>
                    <li>Download your LinkedIn export (Settings → Data Privacy → Get a copy of your data).</li>
                    <li>Drop the <code>Connections.csv</code> — company + role are extracted; names, emails, URLs are stripped in your browser.</li>
                  </ol>
                </div>
                <button
                  onClick={handleImportButtonClick}
                  disabled={networkImporting}
                  className="import-btn"
                >
                  {networkImporting ? (
                    <>
                      <RefreshCw className="spin" size={18} />
                      Importing…
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Import network CSV
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="network-content">
                {/* Network Sub-tabs */}
                <div className="network-subtabs">
                  <button
                    className={networkSubTab === 'overview' ? 'active' : ''}
                    onClick={() => setNetworkSubTab('overview')}
                  >
                    Overview
                  </button>
                  <button
                    className={networkSubTab === 'mentorship' ? 'active' : ''}
                    onClick={() => setNetworkSubTab('mentorship')}
                  >
                    Mentorship
                  </button>
                  <button
                    className={networkSubTab === 'opportunities' ? 'active' : ''}
                    onClick={() => setNetworkSubTab('opportunities')}
                  >
                    Company Reach
                  </button>
                  <button
                    onClick={loadNetworkDashboard}
                    disabled={networkLoading}
                    className="refresh-network-btn"
                    title="Reload network data"
                  >
                    {networkLoading ? <RefreshCw className="spin" size={16} /> : <RefreshCw size={16} />}
                    Refresh
                  </button>
                  <button
                    onClick={handleImportButtonClick}
                    disabled={networkImporting}
                    className="refresh-network-btn"
                    title="Import or re-import your network CSV"
                    style={{ marginLeft: '8px' }}
                  >
                    <Upload size={16} /> Import
                  </button>
                </div>

                {/* Persistent partial-import banner */}
                <ImportStatusBanner
                  status={importStatus}
                  onRetry={handleImportButtonClick}
                />

                {/* Upload panel — appears when user clicks Import + has accepted consent */}
                {showUploadPanel && (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>Upload your network CSV</h3>
                      <button
                        type="button"
                        onClick={() => setShowUploadPanel(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <LinkedInConverter
                      onUploadReady={(file) => uploadNetworkCsv(file)}
                      disabled={networkImporting}
                    />
                    {networkImporting && (
                      <div style={{ marginTop: '12px', color: '#0891b2', fontSize: '13px' }}>
                        <RefreshCw className="spin" size={14} /> Uploading and importing… this can take up to a minute for large networks.
                      </div>
                    )}
                    {networkError && (
                      <div style={{ marginTop: '12px', color: '#b91c1c', fontSize: '13px' }}>
                        {networkError}
                      </div>
                    )}
                    {importResult && importResult.success && (
                      <div style={{ marginTop: '12px', color: '#0f766e', fontSize: '13px' }}>
                        Imported {importResult.rowsImported.toLocaleString()} connections.
                        {importResult.rowsSkipped > 0 && ` Skipped ${importResult.rowsSkipped} (${importResult.rowsSkippedByReason?.malformed || 0} malformed, ${importResult.rowsSkippedByReason?.empty || 0} empty).`}
                      </div>
                    )}
                  </div>
                )}

                {/* Overview — powered by the new NetworkOverview component (recharts) */}
                {networkSubTab === 'overview' && (
                  <div className="network-overview">
                    {!hasNetworkProfile ? (
                      <div className="network-setup" style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                        <h3 style={{ margin: '0 0 8px', fontSize: '20px' }}>Your network intelligence</h3>
                        <p style={{ color: '#64748b', marginBottom: '24px' }}>
                          Import your network CSV to see company distribution, role
                          concentration, and mentorship opportunities in your network.
                          Names, emails, and URLs are stripped in the browser before
                          anything uploads.
                        </p>
                        <button
                          onClick={handleImportButtonClick}
                          disabled={networkImporting}
                          className="import-btn-small"
                          style={{ padding: '10px 24px', fontSize: '15px' }}
                        >
                          <Upload size={16} /> Import network CSV
                        </button>
                      </div>
                    ) : (
                      <NetworkOverview overview={overview} />
                    )}
                  </div>
                )}

                {/* Mentorship — powered by the new NetworkMentorship component */}
                {networkSubTab === 'mentorship' && (
                  <NetworkMentorship mentorship={mentorship} />
                )}

                {/* Company Reach (renamed from "Opportunities" per user
                    decision 2026-08-27) — network concentration, not job
                    listings. Powered by CompanyReach component. */}
                {networkSubTab === 'opportunities' && (
                  <CompanyReach overview={overview} />
                )}
              </div>
            )}
          </div>
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div className="tab-panel goals-panel">
            <XConnectGoals
              user={user}
              entityType={user?.entityType || user?.entity_type}
              vertical={user?.vertical}
              onGoalChange={fetchCommunityData}
            />
          </div>
        )}
      </div>

      <style>{`
        .community-page-container {
          min-height: 100vh;
          background: #f9fafb;
          padding: 24px;
        }

        .community-page-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 16px;
          color: #6b7280;
        }

        .community-page-header {
          max-width: 1200px;
          margin: 0 auto 24px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-title-row {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          color: #6366f1;
        }

        .community-page-header h1 {
          margin: 0;
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
        }

        .community-page-header p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 14px;
        }

        .refresh-btn-large {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #475569;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .refresh-btn-large:hover {
          background: #f8fafc;
          border-color: #6366f1;
          color: #6366f1;
        }

        .community-page-error {
          max-width: 1200px;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
        }

        .community-page-error button {
          margin-left: auto;
          padding: 4px 12px;
          background: none;
          border: 1px solid #dc2626;
          border-radius: 4px;
          color: #dc2626;
          font-size: 13px;
          cursor: pointer;
        }

        .community-page-tabs {
          max-width: 1200px;
          margin: 0 auto 24px;
          display: flex;
          gap: 8px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 0;
          overflow-x: auto;
        }

        .page-tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          color: #64748b;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .page-tab-btn:hover {
          color: #6366f1;
        }

        .page-tab-btn.active {
          color: #6366f1;
          border-bottom-color: #6366f1;
        }

        .tab-badge {
          padding: 2px 8px;
          background: #e2e8f0;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .page-tab-btn.active .tab-badge {
          background: #eef2ff;
          color: #6366f1;
        }

        .community-page-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .tab-panel {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          padding: 24px;
          min-height: 400px;
        }

        .empty-state-large {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .empty-state-large .empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state-large h3 {
          margin: 0 0 8px;
          font-size: 20px;
          color: #374151;
        }

        .empty-state-large p {
          margin: 0 0 24px;
          color: #6b7280;
          max-width: 400px;
        }

        .cta-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: #6366f1;
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .cta-btn:hover {
          background: #4f46e5;
        }

        .suggestions-grid, .requests-grid, .connections-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 16px;
        }

        /* User name styling for cards */
        .suggestion-name, .request-user, .connection-user {
          display: block;
          font-weight: 600;
          font-size: 16px;
          color: #1f2937;
          margin-bottom: 2px;
        }

        /* Avatar image styling */
        img.suggestion-avatar,
        img.request-avatar,
        img.connection-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .section-title {
          margin: 0 0 16px;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
        }

        .requests-section {
          margin-bottom: 32px;
        }

        /* Network Intelligence Styles */
        .network-loading, .network-setup {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .network-setup h3 {
          margin: 16px 0 8px;
          font-size: 20px;
          color: #374151;
        }

        .network-setup p {
          margin: 0 0 24px;
          color: #6b7280;
        }

        .setup-instructions {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          padding: 16px 24px;
          margin-bottom: 24px;
          text-align: left;
        }

        .setup-instructions h4 {
          margin: 0 0 8px;
          color: #1e40af;
        }

        .setup-instructions ol {
          margin: 0;
          padding-left: 20px;
          color: #1e3a8a;
          font-size: 14px;
        }

        .setup-instructions li {
          margin-bottom: 4px;
        }

        .import-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: #2563eb;
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .import-btn:hover:not(:disabled) {
          background: #1d4ed8;
        }

        .import-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .network-subtabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 12px;
        }

        .network-subtabs button {
          padding: 8px 16px;
          background: none;
          border: none;
          color: #6b7280;
          font-weight: 500;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .network-subtabs button:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .network-subtabs button.active {
          background: #eef2ff;
          color: #6366f1;
        }

        .refresh-network-btn {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          color: #6b7280;
          font-size: 13px;
          cursor: pointer;
        }

        .refresh-network-btn:hover {
          background: #e5e7eb;
        }

        .platform-stats-notice {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          border: 1px solid #f59e0b;
          border-radius: 10px;
          padding: 16px 20px;
          margin-bottom: 20px;
        }

        .platform-stats-notice .notice-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .platform-stats-notice .notice-content svg {
          color: #d97706;
          flex-shrink: 0;
        }

        .platform-stats-notice .notice-content strong {
          display: block;
          color: #92400e;
          font-size: 14px;
          margin-bottom: 2px;
        }

        .platform-stats-notice .notice-content p {
          margin: 0;
          color: #a16207;
          font-size: 13px;
        }

        .import-btn-small {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #f59e0b;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .import-btn-small:hover {
          background: #d97706;
        }

        .import-btn-small:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        @media (max-width: 900px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .stat-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-card svg {
          opacity: 0.6;
          margin-bottom: 8px;
        }

        .stat-card.blue svg { color: #3b82f6; }
        .stat-card.green svg { color: #10b981; }
        .stat-card.purple svg { color: #8b5cf6; }
        .stat-card.orange svg { color: #f97316; }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stat-card.blue .stat-value { color: #3b82f6; }
        .stat-card.green .stat-value { color: #10b981; }
        .stat-card.purple .stat-value { color: #8b5cf6; }
        .stat-card.orange .stat-value { color: #f97316; }

        .stat-label {
          font-size: 13px;
          color: #6b7280;
        }

        .graph-link-card {
          background: linear-gradient(135deg, #f5f3ff, #eff6ff);
          border: 2px solid #c4b5fd;
          border-radius: 12px;
          padding: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .graph-info h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 8px;
          color: #5b21b6;
          font-size: 18px;
        }

        .graph-info p {
          margin: 0 0 16px;
          color: #7c3aed;
          font-size: 14px;
        }

        .graph-actions {
          display: flex;
          gap: 12px;
        }

        .graph-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.2s;
        }

        .graph-btn.primary {
          background: #7c3aed;
          border: none;
          color: white;
        }

        .graph-btn.primary:hover {
          background: #6d28d9;
        }

        .graph-btn.secondary {
          background: white;
          border: 1px solid #c4b5fd;
          color: #7c3aed;
        }

        .graph-btn.secondary:hover {
          background: #f5f3ff;
        }

        .graph-icon {
          font-size: 64px;
        }

        @media (max-width: 768px) {
          .graph-icon { display: none; }
        }

        .list-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
        }

        .list-card h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 16px;
          font-size: 16px;
          color: #374151;
        }

        .list-items {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .list-item {
          display: flex;
          align-items: center;
          padding-bottom: 12px;
          border-bottom: 1px solid #f3f4f6;
        }

        .list-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .list-item .rank {
          width: 32px;
          font-size: 13px;
          font-weight: 500;
          color: #9ca3af;
        }

        .list-item .name {
          flex: 1;
          font-weight: 500;
          color: #1f2937;
        }

        .list-item .count {
          font-size: 13px;
          color: #6b7280;
        }

        .positions-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .position-item {
          background: #f9fafb;
          border-radius: 8px;
          padding: 12px;
        }

        .position-rank {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 600;
        }

        .position-title {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
          margin: 4px 0;
        }

        .position-count {
          font-size: 12px;
          color: #6b7280;
        }

        /* Mentorship Styles */
        .mentorship-score-card {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 12px;
          padding: 32px;
          color: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .mentorship-score-card h3 {
          margin: 0 0 4px;
          font-size: 24px;
        }

        .mentorship-score-card p {
          margin: 0;
          opacity: 0.9;
        }

        .mentorship-score-card .score {
          font-size: 64px;
          font-weight: 700;
        }

        .mentorship-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
        }

        .mentorship-section h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 16px;
          font-size: 16px;
          color: #374151;
        }

        .mentorship-section h4 .count {
          font-size: 13px;
          font-weight: 400;
          color: #6b7280;
        }

        .opportunity-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .opportunity-card {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: border-color 0.2s;
        }

        .opportunity-card.learning:hover {
          border-color: #93c5fd;
        }

        .opportunity-card.mentoring:hover {
          border-color: #86efac;
        }

        .opp-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .skill-name {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }

        .priority, .impact {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .priority.high, .impact.high { background: #fee2e2; color: #dc2626; }
        .priority.medium, .impact.medium { background: #fef3c7; color: #d97706; }
        .priority.low, .impact.low { background: #f3f4f6; color: #6b7280; }

        .opportunity-card p {
          margin: 0 0 12px;
          color: #6b7280;
          font-size: 14px;
        }

        .opp-action {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: #3b82f6;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .opp-action:hover {
          background: #2563eb;
        }

        .opp-action.green {
          background: #10b981;
        }

        .opp-action.green:hover {
          background: #059669;
        }

        .insights-card {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 12px;
          padding: 20px;
        }

        .insights-card h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 12px;
          color: #1e40af;
        }

        .insights-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .insight-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          color: #1e3a8a;
          font-size: 14px;
        }

        .insight-item svg {
          flex-shrink: 0;
          margin-top: 2px;
          color: #3b82f6;
        }

        /* Opportunities Styles */
        .network-opportunities h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 20px;
          font-size: 18px;
          color: #374151;
        }

        .opportunities-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .company-opportunity-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: border-color 0.2s;
        }

        .company-opportunity-card:hover {
          border-color: #fdba74;
        }

        .company-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .company-header h5 {
          margin: 0;
          font-size: 20px;
          color: #1f2937;
        }

        .company-header .industry {
          font-size: 13px;
          color: #6b7280;
        }

        .strength-badge {
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .strength-badge.strong {
          background: #dcfce7;
          color: #15803d;
        }

        .strength-badge.medium {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .connections-count {
          margin: 0 0 8px;
          color: #374151;
          font-size: 14px;
        }

        .recommendation {
          margin: 0 0 12px;
          color: #6b7280;
          font-size: 14px;
        }

        .key-connections {
          background: #f9fafb;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
        }

        .key-connections .label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 8px;
        }

        .key-connections .connection {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #4b5563;
          margin-bottom: 4px;
        }

        .company-actions {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: #f97316;
          border: none;
          color: white;
        }

        .action-btn.primary:hover {
          background: #ea580c;
        }

        .action-btn.secondary {
          background: white;
          border: 1px solid #d1d5db;
          color: #374151;
        }

        .action-btn.secondary:hover {
          background: #f9fafb;
        }

        .no-data {
          text-align: center;
          color: #6b7280;
          padding: 32px;
        }

        /* Goals Panel */
        .goals-panel {
          padding: 16px;
        }

        /* Connect Tab Styles */
        .connect-section {
          margin-bottom: 32px;
        }

        .connect-section:last-child {
          margin-bottom: 0;
        }

        .connect-section .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .connect-section .section-title svg {
          color: #6366f1;
        }

        /* Activity Tab Styles */
        .activity-panel {
          min-height: 400px;
        }

        .activity-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .activity-section .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .activity-section .section-title svg {
          color: #6366f1;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .activity-card {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .activity-card:hover {
          border-color: #c7d2fe;
          background: #f8fafc;
        }

        .activity-card.unread {
          background: #eff6ff;
          border-color: #bfdbfe;
        }

        .activity-card.unread:hover {
          background: #dbeafe;
        }

        .activity-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #eef2ff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .activity-icon svg {
          color: #6366f1;
        }

        .activity-card.unread .activity-icon {
          background: #6366f1;
        }

        .activity-card.unread .activity-icon svg {
          color: white;
        }

        .activity-info {
          flex: 1;
          min-width: 0;
        }

        .activity-title {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .activity-message {
          margin: 0 0 8px;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.4;
        }

        .activity-time {
          font-size: 12px;
          color: #9ca3af;
        }

        .unread-indicator {
          width: 10px;
          height: 10px;
          background: #6366f1;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 6px;
        }

        .activity-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .activity-expand-icon {
          color: #9ca3af;
          flex-shrink: 0;
        }

        .activity-details {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .activity-detail-row {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .activity-detail-label {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .activity-detail-value {
          font-size: 13px;
          color: #374151;
          margin: 0;
          line-height: 1.4;
        }

        .activity-detail-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          background: #eef2ff;
          color: #4f46e5;
          width: fit-content;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 640px) {
          .community-page-container {
            padding: 16px;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .suggestions-grid, .requests-grid, .connections-grid {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }

          .positions-grid {
            grid-template-columns: 1fr;
          }

          .company-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

// Suggestion Card Component (Enhanced with Match Explanation v3.0)
const SuggestionCard = ({ suggestion, onConnect, onDismiss, getInitials }) => {
  const [showExplanation, setShowExplanation] = useState(false);

  const matchLevel = suggestion.match_score >= 90 ? 'excellent' :
    suggestion.match_score >= 75 ? 'good' : 'fair';

  // Use suggested_user_name if available, fall back to ID
  const displayName = suggestion.suggested_user_name || suggestion.suggested_user_id;

  // Parse match explanation from suggestion (may be JSON string or object)
  const explanation = React.useMemo(() => {
    if (!suggestion.match_explanation) return null;
    try {
      return typeof suggestion.match_explanation === 'string'
        ? JSON.parse(suggestion.match_explanation)
        : suggestion.match_explanation;
    } catch { return null; }
  }, [suggestion.match_explanation]);

  return (
    <div className={`suggestion-card ${suggestion.is_cross_domain ? 'cross-domain' : ''}`}>
      <div className="suggestion-header">
        {suggestion.suggested_user_avatar ? (
          <img
            src={suggestion.suggested_user_avatar}
            alt={displayName}
            className="suggestion-avatar"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className="suggestion-avatar" style={{ display: suggestion.suggested_user_avatar ? 'none' : 'flex' }}>
          {getInitials(displayName)}
        </div>
        <div className="suggestion-info">
          <span className="suggestion-name">{displayName}</span>
          <span className="suggestion-title">{suggestion.title}</span>
          <div className="suggestion-meta">
            <span className="entity-type">{suggestion.suggested_entity_type}</span>
            <span className="vertical">{suggestion.suggested_vertical}</span>
            {suggestion.is_cross_domain && (
              <span className="cross-domain-badge">Cross-Domain</span>
            )}
          </div>
        </div>
        <div className="suggestion-score">
          <span className={`match-badge ${matchLevel}`}>
            {suggestion.match_score}%
          </span>
          <span className="match-label">Match</span>
        </div>
      </div>

      <p className="suggestion-message">{suggestion.message}</p>

      {suggestion.match_reasons && suggestion.match_reasons.length > 0 && (
        <div className="match-reasons">
          {suggestion.match_reasons.slice(0, 2).map((reason, i) => (
            <span key={i} className="reason-tag">{reason}</span>
          ))}
        </div>
      )}

      {/* Match Explanation v3.0 - shows for any suggestion with score or explanation */}
      {(explanation || suggestion.match_score) && (
        <MatchExplanation
          explanation={explanation}
          score={suggestion.match_score}
          isExpanded={showExplanation}
          onToggle={() => setShowExplanation(!showExplanation)}
          aiMatchSource={suggestion.ai_match_source}
        />
      )}

      <div className="suggestion-actions">
        <button className="connect-btn" onClick={onConnect}>
          <UserPlus size={14} />
          Connect
        </button>
        <button className="dismiss-btn" onClick={onDismiss}>
          <X size={14} />
          Dismiss
        </button>
      </div>
    </div>
  );
};

// Request Card Component
const RequestCard = ({ request, type, onAccept, onReject, getInitials }) => {
  const isIncoming = type === 'incoming';
  // Use enriched other_user_name if available, fall back to raw IDs
  const displayName = request.other_user_name || (isIncoming ? request.from_user_id : request.to_user_id);
  const entityType = request.other_user_entity_type || (isIncoming ? request.from_entity_type : request.to_entity_type);
  const vertical = request.other_user_vertical || (isIncoming ? request.from_vertical : request.to_vertical);
  const avatar = request.other_user_avatar;

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      accepted: '#10b981',
      rejected: '#ef4444',
      cancelled: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  return (
    <div className="request-card">
      <div className="request-header">
        {avatar ? (
          <img
            src={avatar}
            alt={displayName}
            className="request-avatar"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className="request-avatar" style={{ display: avatar ? 'none' : 'flex' }}>
          {getInitials(displayName)}
        </div>
        <div className="request-info">
          <span className="request-user">{displayName}</span>
          <div className="request-meta">
            <span className="entity-type">{entityType}</span>
            {vertical && <span className="vertical">{vertical}</span>}
            <span className="connection-type">{request.connection_type?.replace('_', ' ')}</span>
          </div>
        </div>
        <span
          className="status-badge"
          style={{ backgroundColor: getStatusColor(request.status) + '20', color: getStatusColor(request.status) }}
        >
          {request.status}
        </span>
      </div>

      {request.request_message && (
        <p className="request-message">"{request.request_message}"</p>
      )}

      {isIncoming && request.status === 'pending' && (
        <div className="request-actions">
          <button className="accept-btn" onClick={onAccept}>
            <Check size={14} />
            Accept
          </button>
          <button className="reject-btn" onClick={onReject}>
            <X size={14} />
            Decline
          </button>
        </div>
      )}
    </div>
  );
};

// Connection Card Component (Enhanced with Chat & Profile Sharing v3.0)
const ConnectionCard = ({ connection, currentUserId, getInitials, unreadMsgCount = 0, onChatClosed }) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const handleChatClose = () => {
    setChatOpen(false);
    if (onChatClosed) onChatClosed();
  };

  // Use enriched connected_user_name if available, fall back to raw IDs
  const displayName = connection.connected_user_name ||
    (connection.user_a_id === currentUserId ? connection.user_b_id : connection.user_a_id);
  const entityType = connection.connected_user_entity_type ||
    (connection.user_a_id === currentUserId ? connection.user_b_entity_type : connection.user_a_entity_type);
  const vertical = connection.connected_user_vertical || connection.vertical;
  const avatar = connection.connected_user_avatar;

  return (
    <div className="connection-card">
      {avatar ? (
        <img
          src={avatar}
          alt={displayName}
          className="connection-avatar"
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
      ) : null}
      <div className="connection-avatar" style={{ display: avatar ? 'none' : 'flex' }}>
        {getInitials(displayName)}
      </div>
      <div className="connection-info">
        <span className="connection-user">{displayName}</span>
        <div className="connection-meta">
          <span className="entity-type">{entityType}</span>
          <span className="connection-type">{connection.connection_type?.replace('_', ' ')}</span>
          <span className="vertical">{vertical}</span>
        </div>
      </div>
      <div className="connection-actions" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button
          className="message-btn"
          title="Message"
          onClick={() => setChatOpen(true)}
          style={{ color: '#06b6d4', position: 'relative' }}
        >
          <MessageCircle size={18} />
          {unreadMsgCount > 0 && (
            <span style={{
              position: 'absolute', top: '-4px', right: '-4px',
              background: '#ef4444', color: '#fff', fontSize: '10px',
              fontWeight: 700, borderRadius: '50%', width: '16px', height: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1
            }}>
              {unreadMsgCount > 9 ? '9+' : unreadMsgCount}
            </span>
          )}
        </button>
        <button
          className="message-btn"
          title="View shared profile"
          onClick={() => setProfileOpen(true)}
          style={{ color: '#a855f7' }}
        >
          <ExternalLink size={18} />
        </button>
      </div>

      {/* Direct Chat Modal */}
      <DirectChatModal
        connection={connection}
        isOpen={chatOpen}
        onClose={handleChatClose}
      />

      {/* Profile Share Modal */}
      <ProfileShareModal
        connection={connection}
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
      />
    </div>
  );
};

// Format relative date/time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Activity Card Component - with expandable details
const ActivityCard = ({ notification, onMarkRead }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const getTypeIcon = (type) => {
    if (type?.includes('request')) return UserPlus;
    if (type?.includes('connection')) return Link2;
    if (type?.includes('suggestion')) return Sparkles;
    if (type?.includes('goal')) return Target;
    return Bell;
  };

  const Icon = getTypeIcon(notification.notification_type);
  const meta = typeof notification.metadata === 'string'
    ? (() => { try { return JSON.parse(notification.metadata); } catch { return null; } })()
    : notification.metadata || null;
  const hasDetails = !!(meta?.goalText || meta?.goalType || meta?.vertical);

  const handleClick = () => {
    if (!notification.is_read) onMarkRead();
    if (hasDetails) setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={`activity-card ${notification.is_read ? 'read' : 'unread'}`}
      onClick={handleClick}
    >
      <div className="activity-icon">
        <Icon size={18} />
      </div>
      <div className="activity-info">
        <div className="activity-header-row">
          <span className="activity-title">{notification.title}</span>
          {hasDetails && (
            <ChevronRight
              size={16}
              className="activity-expand-icon"
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
            />
          )}
        </div>
        <p className="activity-message">{notification.message}</p>
        <span className="activity-time">
          {formatRelativeTime(notification.created_at)}
        </span>

        {/* Expanded goal details */}
        {isExpanded && meta && (
          <div className="activity-details">
            {meta.goalText && (
              <div className="activity-detail-row">
                <span className="activity-detail-label">Goal</span>
                <p className="activity-detail-value">{meta.goalText}</p>
              </div>
            )}
            {meta.goalType && (
              <div className="activity-detail-row">
                <span className="activity-detail-label">Type</span>
                <span className="activity-detail-value" style={{ textTransform: 'capitalize' }}>
                  {meta.goalType.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {meta.vertical && (
              <div className="activity-detail-row">
                <span className="activity-detail-label">Domain</span>
                <span className="activity-detail-badge">{meta.vertical}</span>
              </div>
            )}
            {meta.goalDescription && (
              <div className="activity-detail-row">
                <span className="activity-detail-label">Description</span>
                <p className="activity-detail-value" style={{ color: '#6b7280' }}>{meta.goalDescription}</p>
              </div>
            )}
          </div>
        )}
      </div>
      {!notification.is_read && <span className="unread-indicator" />}
    </div>
  );
};

export default CommunityPage;
